import React, { useState } from 'react';
import { motion } from 'framer-motion';

const SubscriptionPlans: React.FC = () => {
    const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

    const plans = [
        {
            id: 'starter',
            name: 'Starter',
            price: {
                monthly: 'Free',
                yearly: 'Free'
            },
            description: 'Perfect for beginners in crypto trading',
            features: [
                'Basic portfolio tracking',
                'Real-time market data',
                'Basic AI market analysis'
            ],
            cta: 'Get Started',
            popular: false
        },
        {
            id: 'professional',
            name: 'Professional',
            price: {
                monthly: '$29',
                yearly: '$290'
            },
            period: {
                monthly: '/month',
                yearly: '/year'
            },
            description: 'Advanced features for serious traders',
            features: [
                'Advanced AI insights & predictions',
                'AI-powered trading signals',
                'Portfolio optimization'
            ],
            cta: 'Start Free Trial',
            popular: true,
            savings: 'Save 20%'
        },
        {
            id: 'enterprise',
            name: 'Enterprise',
            price: {
                monthly: 'Custom',
                yearly: 'Custom'
            },
            description: 'Tailored solutions for institutions',
            features: [
                'Custom AI model training',
                'Institutional AI insights',
                'White-label solutions'
            ],
            cta: 'Contact Sales',
            popular: false
        }
    ];

    return (
        <section id="subscription" className="section bg-white py-20">
            <div className="container-custom">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="inline-block px-3 py-1 bg-blue-50 rounded-full text-blue-600 text-sm font-medium mb-4">
                            Subscription Plans
                        </div>
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your <span className="text-blue-600">Perfect Plan</span></h2>
                        <p className="text-gray-600">
                            Select the plan that best fits your trading needs. All plans include our core features with different levels of advanced functionality.
                        </p>
                    </motion.div>

                    {/* Billing Toggle */}
                    <div className="mt-8 flex items-center justify-center gap-4">
                        <span className={`text-sm ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>Monthly</span>
                        <button
                            onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
                            className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200"
                        >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                        <span className={`text-sm ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-500'}`}>Yearly</span>
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    {plans.map((plan, index) => (
                        <motion.div
                            key={plan.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            className={`relative ${plan.popular ? 'md:-mt-8 md:mb-8' : ''}`}
                        >
                            {plan.popular && (
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                    <span className="px-4 py-1 bg-blue-600 text-white text-sm font-medium rounded-full">
                                        Most Popular
                                    </span>
                                </div>
                            )}
                            <div
                                className={`h-[400px] bg-white rounded-2xl p-8 border-2 transition-all duration-300 flex flex-col
                                    ${selectedPlan === plan.id
                                        ? 'border-blue-600 shadow-lg shadow-blue-100'
                                        : plan.popular
                                            ? 'border-blue-600 hover:shadow-lg hover:shadow-blue-100'
                                            : 'border-gray-200 hover:border-blue-600 hover:shadow-lg hover:shadow-blue-100'
                                    }`}
                                onClick={() => setSelectedPlan(plan.id)}
                            >
                                <div>
                                    <h3 className="text-2xl font-bold mb-2 text-gray-900">{plan.name}</h3>
                                    <div className="flex items-baseline mb-2">
                                        <span className="text-4xl font-bold text-gray-900">{plan.price[billingCycle]}</span>
                                        {plan.period && (
                                            <span className="text-gray-500 ml-1">{plan.period[billingCycle]}</span>
                                        )}
                                    </div>
                                    {plan.savings && billingCycle === 'yearly' && (
                                        <span className="inline-block px-2 py-1 bg-green-100 text-green-600 text-xs font-medium rounded-full">
                                            {plan.savings}
                                        </span>
                                    )}
                                    <p className="text-gray-600 mt-2">{plan.description}</p>
                                </div>

                                <ul className="space-y-4 my-8">
                                    {plan.features.map((feature, i) => (
                                        <li key={i} className="flex items-start">
                                            <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                            </svg>
                                            <span className="text-gray-600">{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-auto">
                                    {selectedPlan === plan.id ? (
                                        <motion.button
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            Subscribe Now
                                        </motion.button>
                                    ) : (
                                        <motion.button
                                            className="w-full py-3 bg-gray-50 text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            {plan.cta}
                                        </motion.button>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default SubscriptionPlans; 